import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import SwitchField from '@components/Form/SwitchField';
import TextField from '@components/Form/TextField';
import SectionBreakTitle, { SectionColor } from '@components/SectionBreakTitle';
import { SubscriptionFormProps } from '@components/SubscriptionForm';
import { useAppContext } from '@contexts/AppContext';
import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import { useEventTracking, useUserCreate } from '@hooks';
import { Theme } from '@interfaces/global';
import { RECAPTCHA_SITE_KEY } from '@src/env';
import { ClickTarget, TrackingLocation, TypingInput, click, typing } from '@tracking';
import { LeadType } from '@utils/backend-api/users';
import { YOUNG_PROFESSIONALS_PLAN } from '@utils/constants';
import { splitInitialDob } from '@utils/dates';
import { extractQueryParams } from '@utils/queryStringHelper';

import DateOfBirthField from '../OfferSelection/DateOfBirthField';
import validationSchema from '../OfferSelection/validationSchema';

interface FormValues {
  day: { label: number; value: number };
  dob: string;
  email: string;
  gdprOptIn: boolean;
  month: { label: string; value: number };
  name: string;
  userEmail?: string;
  year: { label: number; value: number };
}

interface SignupFormProps extends Omit<SubscriptionFormProps, 'buttonText'> {
  formLocation: string;
}

const SignupForm = ({
  formLocation,
  referringSubscriberGuid,
  selectedOffer: { code, isPro },
}: SignupFormProps) => {
  const {
    user: { dob, email, name },
    setUser,
  } = useSubscribeForm();
  const { isGdprRequired } = useAppContext();
  const { trackEvent } = useEventTracking({ location: TrackingLocation.SignupForm });
  const isTypingName = useRef(false);
  const isTypingEmail = useRef(false);
  const [isSignupFormSubmitted, setIsSignupFormSubmitted] = useState(false);
  const { mutateAsync: createUser, isLoading } = useUserCreate();
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const { article_id: referringArticleId } = extractQueryParams(['article_id']);
  const { initialDay, initialMonth, initialYear } = splitInitialDob(dob);
  const isYPPlan = code.includes(YOUNG_PROFESSIONALS_PLAN);
  const leadType = referringSubscriberGuid ? LeadType.REFERRAL_PAGE : LeadType.SUBSCRIBE_PAGE;
  const Button = isPro ? ProButton : PrimaryButton;

  // Passed userEmail to the form for email validation initial state
  const initialState = {
    day: initialDay,
    dob,
    email: email || '',
    gdprOptIn: false,
    month: initialMonth,
    name: name || '',
    userEmail: email,
    year: initialYear,
  };

  useEffect(() => {
    if (setIsSignupFormSubmitted) {
      if (email && name && ((isYPPlan && dob) || !isYPPlan)) {
        setIsSignupFormSubmitted(true);
      } else {
        setIsSignupFormSubmitted(false);
      }
    }
  }, [email, name, dob, isYPPlan, setIsSignupFormSubmitted]);

  const handleCreateUser = async ({ day, email, gdprOptIn, month, name, year }: FormValues) => {
    try {
      const refToken = await reCaptchaRef.current.executeAsync();
      const dobString = isYPPlan ? `${year.value}-${month.value}-${day.value}` : null;
      const createUserParams = {
        declined_opt_in: isGdprRequired ? !gdprOptIn : false,
        dob: dobString,
        code,
        email,
        form_location: formLocation,
        lead_type: leadType,
        name,
        opt_in_required: isGdprRequired,
        page: window.location.pathname,
        recaptcha_token: refToken,
        referring_article_id: referringArticleId,
      };
      const { stripeCustomerId } = await createUser(createUserParams);
      setUser({ dob: dobString, email, name, stripeCustomerId });
      trackEvent(click({ clickTarget: ClickTarget.Continue }));
    } catch (error) {
      console.error(error);
    } finally {
      reCaptchaRef.current.reset();
    }
  };

  const handleTextFieldChange = (input: TypingInput) => {
    const isTypingRef = {
      [TypingInput.FullName]: isTypingName,
      [TypingInput.Email]: isTypingEmail,
    };
    const isTyping = isTypingRef[input];

    if (!isTyping.current) {
      isTyping.current = true;
      trackEvent(typing({ inputTarget: input }));
    }
  };

  return (
    <div className="transition-all duration-500 ease-in">
      <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
        <Formik
          enableReinitialize
          initialValues={initialState}
          onSubmit={handleCreateUser}
          validationSchema={validationSchema(isYPPlan)}
        >
          {({ handleSubmit, errors, touched }) => (
            <Form
              className="account-info m-auto max-w-md sm:max-w-full"
              noValidate
              onSubmit={handleSubmit}
            >
              <SectionBreakTitle
                title={email ? 'Your account' : 'Create your account'}
                titleClassName="text-black"
                className="mb-24"
                color={isPro ? SectionColor.Gold : SectionColor.Magenta}
              />
              <TextField
                className="mb-16"
                name="name"
                placeholder="Full Name"
                disabled={!!name}
                onChangeCapture={() => handleTextFieldChange(TypingInput.FullName)}
              />
              <TextField
                className={isYPPlan ? 'mb-16' : ''}
                name="email"
                placeholder="Email"
                disabled={!!email}
                onChangeCapture={() => handleTextFieldChange(TypingInput.Email)}
              />
              {isYPPlan && (
                <DateOfBirthField
                  disableDobInput={!!dob}
                  error={
                    touched.month && touched.day && touched.year && errors?.dob ? errors.dob : null
                  }
                />
              )}
              {isGdprRequired && !email && (
                <SwitchField
                  colorTheme={Theme.Light}
                  className="gdpr mt-16"
                  label="Receive breaking news, relevant articles, and special event invites from The Information"
                  name="gdprOptIn"
                />
              )}
              <Button
                as="button"
                id="signupBtn"
                disabled={isLoading || isSignupFormSubmitted}
                isLoading={isLoading}
                type="submit"
                className="mt-24 sm:mb-0"
                fullWidth
              >
                Continue
              </Button>
            </Form>
          )}
        </Formik>
      </ReCAPTCHA>
    </div>
  );
};

export default SignupForm;
